/**
 * The collage question's step (collage/step.ts): the picker, the progress bar,
 * the tray of 56 px thumbnails and the state lines. A self-contained block on
 * `.pz-collage*` classes, appended to the widget's sheet by styles.ts. The
 * board ("Raspored slika") has its own module (board-styles.ts).
 *
 * Touch targets are at least 44 px; the tray wraps five to a row at phone
 * width and never widens the page.
 */
export const COLLAGE_STEP_STYLES = `
/* ── The photo collage step (collage/step.ts) ─────────────────────────────── */
.pz-collage { display: grid; gap: 10px; min-width: 0; }
.pz-collage .pz-hint { overflow-wrap: anywhere; }
.pz-collage-pick { min-height: 56px; }
.pz-collage-pick .pz-collage-btn-text { min-width: 0; overflow-wrap: anywhere; }
.pz-collage-tip, .pz-collage-wifi { margin-top: -2px; }

.pz-collage-progress { display: grid; gap: 6px; }
.pz-collage-progress-text { margin: 0; font-size: 14px; font-weight: 600; color: var(--pz-ink-2);
  font-variant-numeric: tabular-nums; }
.pz-collage-bar { position: relative; height: 6px; overflow: hidden; border-radius: 99px;
  background: var(--pz-surface-3); }
.pz-collage-bar-fill { position: absolute; left: 0; top: 0; bottom: 0; width: 3%; border-radius: inherit;
  background: var(--pz-accent); transition: width .35s var(--pz-ease); }

.pz-collage-state { display: grid; gap: 8px; }
.pz-collage-headline { margin: 0; font-size: 14.5px; font-weight: 650; line-height: 1.35; color: var(--pz-ink); }
.pz-collage-state.is-complete .pz-collage-headline { color: var(--pz-success); }
.pz-collage-state.is-incomplete .pz-collage-headline { color: var(--pz-warn); }
.pz-collage-open { width: 100%; min-height: 48px; }
.pz-collage-open svg, .pz-collage-actions svg { width: 18px; height: 18px; flex-shrink: 0; }
.pz-collage-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.pz-collage-actions > button { flex: 1 1 140px; min-height: 44px; }
.pz-collage-btn-text { min-width: 0; }

.pz-collage-fail { display: grid; gap: 6px; padding: 10px 12px; border-radius: var(--pz-radius);
  background: var(--pz-surface-2); border: 1px solid var(--pz-line); }
.pz-collage-fail-text { margin: 0; font-size: 13.5px; font-weight: 600; color: var(--pz-ink-2); }
.pz-collage-resend { justify-self: start; min-height: 44px; }
.pz-collage-unusable { margin: 0; padding-left: 18px; font-size: 12.5px; color: var(--pz-muted);
  line-height: 1.45; overflow-wrap: anywhere; }

.pz-collage-notice { color: var(--pz-ink-2); }

/* The tray: 56 px thumbnails, five to a row on a phone. */
.pz-collage-tray { display: grid; grid-template-columns: repeat(auto-fill, minmax(56px, 1fr)); gap: 6px;
  max-width: 100%; }
@media (max-width: 420px) { .pz-collage-tray { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
.pz-collage-thumb { position: relative; display: block; width: 100%; aspect-ratio: 1 / 1; min-height: 44px;
  padding: 0; overflow: hidden; cursor: pointer; background: var(--pz-surface-3);
  border: 1.5px solid var(--pz-line); border-radius: var(--pz-radius-sm);
  -webkit-tap-highlight-color: transparent; }
.pz-collage-thumb:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-collage-thumb img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
.pz-collage-thumb::after { position: absolute; right: 3px; bottom: 3px; display: grid; place-items: center;
  width: 18px; height: 18px; border-radius: 50%; font-size: 11px; font-weight: 800; line-height: 1;
  box-shadow: 0 1px 2px rgba(0,0,0,.25); }
.pz-collage-thumb.is-done::after { content: "✓"; color: #fff; background: var(--pz-success); }
.pz-collage-thumb.is-spare { border-style: dashed; }
.pz-collage-thumb.is-spare img { opacity: .7; }
.pz-collage-thumb.is-spare::after { content: ""; display: none; }
.pz-collage-thumb.is-sending::before { content: ""; position: absolute; left: 50%; top: 50%; width: 20px; height: 20px;
  margin: -10px 0 0 -10px; border-radius: 50%; border: 2.5px solid var(--pz-line-2);
  border-top-color: var(--pz-accent); animation: pz-collage-spin .8s linear infinite; }
.pz-collage-thumb.is-failed { border-color: var(--pz-danger); }
.pz-collage-thumb.is-failed::after { content: "!"; color: #fff; background: var(--pz-danger); }
.pz-collage-thumb.is-unusable { cursor: default; opacity: .6; border-style: dashed; }
.pz-collage-thumb.is-unusable::after { content: "!"; color: #fff; background: var(--pz-muted); }
@keyframes pz-collage-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .pz-collage-thumb.is-sending::before { animation-duration: 2.4s; } }
.pz-collage-more { justify-self: start; min-height: 44px; padding: 0 2px; font: inherit; font-size: 13.5px;
  font-weight: 600; color: var(--pz-accent-ink); background: none; border: 0; cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px; }
.pz-collage-spares, .pz-collage-faces, .pz-collage-quality { color: var(--pz-ink-2); }

/* "Uredi raspored" on the preview (main.ts syncCollageCap): in the caption on
   the page; in the popup, whose caption is hidden to save height, as a chip
   over the bottom-right corner of the picture. */
.pz-preview-collage { display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: 6px 10px;
  cursor: pointer; font: inherit; font-size: 12.5px; font-weight: 650; color: var(--pz-ink-2);
  background: transparent; border: 0; border-radius: var(--pz-radius-sm); -webkit-tap-highlight-color: transparent; }
.pz-preview-collage:hover { background: var(--pz-accent-soft); color: var(--pz-accent-ink); }
.pz-preview-collage:focus-visible { outline: 2px solid var(--pz-accent); outline-offset: 2px; }
.pz-preview-collage .pz-ic { font-size: 15px; }
.pz-modal-preview .pz-preview.has-collage .pz-preview-cap { display: block !important; position: absolute; right: 8px;
  bottom: 8px; z-index: 3; min-height: 0; padding: 0; background: none; border: 0; }
.pz-modal-preview .pz-preview.has-collage .pz-preview-cap-text { display: none; }
.pz-modal-preview .pz-preview.has-collage .pz-preview-cap-actions { display: inline-flex !important; }
.pz-modal-preview .pz-preview.has-collage .pz-preview-cap-actions > :not(.pz-preview-collage) { display: none !important; }
.pz-modal-preview .pz-preview-collage { min-height: 36px; padding: 6px 12px; border-radius: 99px; color: var(--pz-ink);
  background: rgba(255,255,255,.94); box-shadow: 0 1px 3px rgba(16,16,32,.28); }
`;
